'use client';

import React from 'react';

import type { Price } from '@nutralab/api';
import { PriceTimer, Text } from '@nutralab/ui';

import { classNames } from '@m1work/shared-utils';

import styles from './SearchResultPrice.module.scss';

import { usePriceTimer } from '@/shared/hooks';
import { getCurrencySign } from '@/shared/utils';

interface SearchResultPriceProps {
    price: Price;
    oldPrice: Price | null;
    expiresAt: string | null;
    className?: string;
}

/** The price of a search result: the current price, the old one and the timer of a limited offer. */
export function SearchResultPrice({
    price,
    oldPrice,
    expiresAt,
    className,
}: SearchResultPriceProps): React.ReactElement {
    const {
        price: amount,
        oldPrice: oldAmount,
        secondsLeft,
    } = usePriceTimer({
        price: price.amount,
        oldPrice: oldPrice?.amount ?? null,
        expiresAt,
    });
    const currencySign = getCurrencySign(price.currency);

    return (
        <div className={classNames(styles.price, className)}>
            <div className={styles.amounts}>
                <Text as="span" weight="semibold" className={styles.amount} truncate>
                    {amount} {currencySign}
                </Text>

                {oldAmount && (
                    <Text as="span" size="sm" color="muted" className={styles['old-price']} truncate>
                        {oldAmount} {currencySign}
                    </Text>
                )}
            </div>

            {secondsLeft !== null && <PriceTimer secondsLeft={secondsLeft} size="xs" />}
        </div>
    );
}
